import React, { FC, ReactNode, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Box, 
  Container, 
  AppBar, 
  Toolbar, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  CssBaseline
} from '@mui/material';
import { Menu as MenuIcon, Users, X, Contact as FileContract, Laptop, LogOut, Settings, User, Building2, LayoutDashboard, ChevronLeft } from 'lucide-react';
import newLogo from '../assets/logo_m.png';
import { useAuth } from '../contexts/AuthContext';
import { allMenuItems, MenuItemConfig } from '../config/menuConfig';

interface LayoutProps {
  children: ReactNode;
}

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

const Layout: FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user, permissions, logout, hasPermission, isAuthenticated } = useAuth();

  const visibleMenuItems = useMemo(() => {
      return allMenuItems.filter(item => 
          !item.requiredPermission || hasPermission(item.requiredPermission)
      );
  }, [permissions, hasPermission]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const getUserInitials = (name: string | undefined): string => {
    if (!name) return '?';
    const nameParts = name.split(' ');
    const firstNameInitial = nameParts[0]?.[0] || '';
    const lastNameInitial = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';
    return `${firstNameInitial}${lastNameInitial}`.toUpperCase();
  };

  const drawerContent = (
    <>
      <Toolbar variant="dense" />
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}/>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowX: 'hidden', overflowY: 'auto' }}>
        <List component="nav" sx={{ pt: 0, flexGrow: 1 }}>
          {visibleMenuItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location.pathname === '/'
              ? item.path === '/'
              : item.path !== '/' && location.pathname.startsWith(item.path);

            return (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <ListItemButton 
                  onClick={() => navigate(item.path)}
                  title={item.text}
                  selected={isActive}
                  sx={{
                    minHeight: 38,
                    justifyContent: drawerOpen ? 'initial' : 'center',
                    px: 2,
                    py: 0.5,
                    mb: 0.5,
                    borderRadius: drawerOpen ? 1 : 0,
                    mx: drawerOpen ? 1 : 0,
                    color: '#fff',
                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      fontWeight: 'bold'
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: drawerOpen ? 2 : 'auto',
                      justifyContent: 'center',
                      color: 'inherit'
                    }}
                  >
                    { IconComponent ? <IconComponent size={20} /> : null }
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text} 
                    sx={{
                      opacity: drawerOpen ? 1 : 0, 
                      fontSize: '0.9rem',
                      color: 'inherit',
                      '& .MuiTypography-root': {
                        fontSize: '0.875rem',
                        fontWeight: isActive ? 'bold' : 'normal'
                      }
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', my: 1 }} />
        <Box sx={{ mt: 'auto' }}>
          <List>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton 
                onClick={handleLogout}
                title="Sair"
                sx={{
                  minHeight: 38,
                  justifyContent: drawerOpen ? 'initial' : 'center',
                  px: 2,
                  py: 0.5,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: drawerOpen ? 2 : 'auto',
                    justifyContent: 'center',
                    color: 'inherit'
                  }}
                >
                  <LogOut size={20} />
                </ListItemIcon>
                <ListItemText 
                  primary="Sair" 
                  sx={{
                    opacity: drawerOpen ? 1 : 0, 
                    fontSize: '0.9rem',
                    color: '#fff',
                    '& .MuiTypography-root': {
                      fontSize: '0.9rem',
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar variant="dense">
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: 'inline-flex' }
            }}
          >
            <MenuIcon size={24} />
          </IconButton>
          <img src={newLogo} alt="Siga RH Logo" style={{ height: '32px', marginRight: 12 }} />
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant={isMobile ? 'h6' : 'h5'} 
              component="div" 
              noWrap
              sx={{ 
                fontSize: isMobile ? '1rem' : '1.2rem',
                lineHeight: 1.2
              }}
            >
              Siga RH
            </Typography>
            {user && (
              <Typography 
                variant="caption" 
                display="block"
                noWrap 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1,
                  fontSize: '0.75rem'
                }}
              >
                {user.name}
              </Typography>
            )}
          </Box>
          
          <IconButton
            onClick={handleUserMenuOpen}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={Boolean(anchorEl) ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl) ? 'true' : undefined}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
              {getUserInitials(user?.name)}
            </Avatar>
          </IconButton>
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
            onClick={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogOut size={20} />
              </ListItemIcon>
              <ListItemText>Sair</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open={drawerOpen}
        sx={{
          width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerOpen ? drawerWidth : collapsedDrawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            borderRight: 'none',
            backgroundColor: theme.palette.primary.main,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: drawerOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
            display: 'flex',
            flexDirection: 'column'
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 2,
          height: '100vh',
          paddingTop: '48px',
          overflowY: 'auto'
        }}
      >
        <Container maxWidth={false} sx={{ pt: 0, pb: 2, height: 'calc(100% - 48px)', overflowY: 'auto' }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;